// client/src/pages/documents/DocumentsList.js

import React, { useState, useEffect, useCallback } from 'react';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import Modal from 'react-bootstrap/Modal';
import { FaEye, FaPen, FaTrash, FaPlus } from 'react-icons/fa';
import useDocuments from '../../hooks/useDocuments';
import PaginationControl from '../../components/PaginationControl';

const CATEGORY_LABELS = {
  PAYSLIP: 'Fiche de paie',
  BANK_STATEMENT: 'Relevé bancaire',
  OTHER: 'Autre'
};

const CATEGORY_BADGE = {
  PAYSLIP: 'primary',
  BANK_STATEMENT: 'info',
  OTHER: 'secondary'
};

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    : '—';

const parseTagsInput = (value) =>
  value
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

function UploadModal({ show, onClose, category, onSubmit, saving }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [documentDate, setDocumentDate] = useState('');
  const [formCategory, setFormCategory] = useState(category || 'OTHER');

  useEffect(() => {
    if (show) {
      setFile(null);
      setTitle('');
      setTagsInput('');
      setDocumentDate('');
      setFormCategory(category || 'OTHER');
    }
  }, [show, category]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !title.trim()) return;
    await onSubmit({
      file,
      title: title.trim(),
      category: formCategory,
      tags: parseTagsInput(tagsInput),
      documentDate: documentDate || undefined
    });
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Ajouter un document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Fichier PDF</Form.Label>
            <Form.Control
              type="file"
              accept=".pdf"
              onChange={(e) => setFile(e.target.files[0] || null)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Titre</Form.Label>
            <Form.Control
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex : Fiche de paie - juillet 2026"
              required
            />
          </Form.Group>
          {!category && (
            <Form.Group className="mb-3">
              <Form.Label>Catégorie</Form.Label>
              <Form.Select value={formCategory} onChange={(e) => setFormCategory(e.target.value)}>
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Tags (séparés par des virgules)</Form.Label>
            <Form.Control
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="Ex : salaire, 2026"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Date du document (optionnel)</Form.Label>
            <Form.Control
              type="date"
              value={documentDate}
              onChange={(e) => setDocumentDate(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={saving || !file || !title.trim()}>
            {saving ? <Spinner as="span" animation="border" size="sm" /> : 'Envoyer'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function EditModal({ doc, onClose, onSubmit, saving }) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('OTHER');
  const [tagsInput, setTagsInput] = useState('');
  const [documentDate, setDocumentDate] = useState('');

  useEffect(() => {
    if (doc) {
      setTitle(doc.title);
      setCategory(doc.category);
      setTagsInput((doc.tags || []).join(', '));
      setDocumentDate(doc.documentDate ? doc.documentDate.slice(0, 10) : '');
    }
  }, [doc]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSubmit(doc.id, {
      title: title.trim(),
      category,
      tags: parseTagsInput(tagsInput),
      documentDate: documentDate || ''
    });
  };

  return (
    <Modal show={Boolean(doc)} onHide={onClose} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Modifier le document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Titre</Form.Label>
            <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Catégorie</Form.Label>
            <Form.Select value={category} onChange={(e) => setCategory(e.target.value)}>
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Tags (séparés par des virgules)</Form.Label>
            <Form.Control value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Date du document</Form.Label>
            <Form.Control
              type="date"
              value={documentDate}
              onChange={(e) => setDocumentDate(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={saving || !title.trim()}>
            {saving ? <Spinner as="span" animation="border" size="sm" /> : 'Enregistrer'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function ViewModal({ title, url, onClose }) {
  return (
    <Modal show={Boolean(url)} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        {url && (
          <iframe
            title={title}
            src={url}
            style={{ width: '100%', height: '75vh', border: 'none' }}
          />
        )}
      </Modal.Body>
    </Modal>
  );
}

function DocumentsList({ category }) {
  const {
    documents,
    pagination,
    loading,
    error,
    fetchDocuments,
    uploadDocument,
    updateDocument,
    deleteDocument,
    viewDocument
  } = useDocuments();

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [tag, setTag] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [viewing, setViewing] = useState(null); // { title, url }

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [category, tag, from, to]);

  const reload = useCallback(() => {
    fetchDocuments({ category: category || undefined, tag, from, to, search }, page, 20).catch(
      () => {}
    );
  }, [fetchDocuments, category, tag, from, to, search, page]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleUpload = async (payload) => {
    await uploadDocument(payload);
    setShowUploadModal(false);
    reload();
  };

  const handleEditSubmit = async (id, data) => {
    await updateDocument(id, data);
    setEditingDoc(null);
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Supprimer « ${doc.title} » ? Cette action est irréversible.`)) return;
    await deleteDocument(doc.id);
  };

  const handleView = async (doc) => {
    try {
      const url = await viewDocument(doc.id);
      setViewing({ title: doc.title, url });
    } catch {
      // erreur affichée via le hook
    }
  };

  const closeViewer = () => {
    if (viewing?.url) URL.revokeObjectURL(viewing.url);
    setViewing(null);
  };

  return (
    <div>
      <h1>{category ? CATEGORY_LABELS[category] : 'Tous les documents'}</h1>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="border shadow-sm mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Label className="small text-muted mb-1">Recherche</Form.Label>
              <Form.Control
                type="search"
                placeholder="Titre ou nom de fichier…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="small text-muted mb-1">Tag</Form.Label>
              <Form.Control
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                placeholder="Ex : salaire"
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small text-muted mb-1">Du</Form.Label>
              <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label className="small text-muted mb-1">Au</Form.Label>
              <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Col>
            <Col md={1} className="text-end">
              <Button
                variant="primary"
                onClick={() => setShowUploadModal(true)}
                title="Ajouter un document"
              >
                <FaPlus />
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading.list ? (
        <Spinner animation="border" />
      ) : documents.length === 0 ? (
        <Alert variant="info">Aucun document ne correspond à ces filtres.</Alert>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Titre</th>
                <th>Catégorie</th>
                <th>Tags</th>
                <th>Date document</th>
                <th>Date d&apos;envoi</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.id}>
                  <td>{doc.title}</td>
                  <td>
                    <Badge bg={CATEGORY_BADGE[doc.category]}>{CATEGORY_LABELS[doc.category]}</Badge>
                  </td>
                  <td>
                    {(doc.tags || []).map((t) => (
                      <Badge key={t} bg="light" text="dark" className="me-1 border">
                        {t}
                      </Badge>
                    ))}
                  </td>
                  <td>{formatDate(doc.documentDate)}</td>
                  <td>{formatDate(doc.createdAt)}</td>
                  <td className="text-end">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-1"
                      onClick={() => handleView(doc)}
                      title="Voir"
                    >
                      <FaEye />
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-1"
                      onClick={() => setEditingDoc(doc)}
                      title="Éditer"
                    >
                      <FaPen />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(doc)}
                      title="Supprimer"
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="d-flex justify-content-center">
            <PaginationControl
              currentPage={page}
              totalPages={pagination.totalPages}
              onPageChange={setPage}
            />
          </div>
        </>
      )}

      <UploadModal
        show={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        category={category}
        onSubmit={handleUpload}
        saving={loading.upload}
      />
      <EditModal
        doc={editingDoc}
        onClose={() => setEditingDoc(null)}
        onSubmit={handleEditSubmit}
        saving={loading.update}
      />
      <ViewModal title={viewing?.title} url={viewing?.url} onClose={closeViewer} />
    </div>
  );
}

export default DocumentsList;
